import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { isDefined } from "@omni/utils/helper.utils";
import classnames from "classnames";
import { useRef } from "react";

import { Switch } from "@/components/Form/Base/switch.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useOutsideAlerter } from "@/utils/hooks/useOutsideAlerter";

import "./featureSwitch.scss";
import { Button } from "@omni/ui/components/Button/button.component";

type FeatureSwitchComponentProps = {
    isOpen: boolean;
    features: Array<{
        name: FeatureToggleKey;
        value: boolean;
        serverValue: boolean | null;
    }>;
    bottomControlsVisible: boolean;
    onToggle: (state?: boolean) => void;
    onToggleFeature: (name: FeatureToggleKey, value: boolean) => void;
};

const FeatureSwitchComponent = ({
    isOpen,
    features,
    bottomControlsVisible,
    onToggle,
    onToggleFeature
}: FeatureSwitchComponentProps) => {
    const { t } = useGranite();
    const ref = useRef<HTMLDivElement | null>(null);

    useOutsideAlerter(() => {
        onToggle(false);
    }, ref);

    return (
        <div
            className={classnames(
                "feature-switch btn-group dropup",
                bottomControlsVisible && "feature-switch--bottomControlsVisible"
            )}
            ref={ref}
        >
            <Button
                type="button"
                color="warning"
                onClick={() => onToggle()}
                data-toggle="dropdown"
            >
                <i className="las la-sliders-h"></i>
            </Button>
            <div
                className={classnames("dropdown-menu feature-switch__menu", {
                    show: isOpen
                })}
            >
                {features.map((feature) => {
                    return (
                        <div
                            key={feature.name}
                            className={
                                "dropdown-item d-flex flex-column flex-md-row justify-content-between align-items-md-center"
                            }
                        >
                            <Switch
                                name={feature.name}
                                value={feature.value}
                                label={t(`featureSwitch.${feature.name}`)}
                                onToggle={() =>
                                    onToggleFeature(
                                        feature.name,
                                        !feature.value
                                    )
                                }
                            />
                            {isDefined(feature.serverValue) && (
                                <div className={"d-flex align-items-center"}>
                                    <span className={"d-block pr-2"}>
                                        Server value
                                    </span>
                                    <span
                                        className={classnames(
                                            "feature-switch__menu__item__server-badge rounded text-white small text-center",
                                            {
                                                "bg-success":
                                                    feature.serverValue,
                                                "bg-danger":
                                                    !feature.serverValue
                                            }
                                        )}
                                    >
                                        {String(feature.serverValue)}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export { FeatureSwitchComponent };
